import * as React from 'react';
import { FixMeLater } from '../../../utils';
import { useStyles } from './CardHeader.styles';
import { StylerBaseProps } from '../../Styler';
import { Avatar } from '../../Avatar/Avatar';
import { TickIcon } from '../../../icons/TickIcon';
import { useCard } from '../Card.context';
import { Anchor, TypographyBody, TypographyBodyProps } from '../../Typography';
import { Stacker } from '../../Stacker/Stacker';
import { BinIcon } from '../../../icons/BinIcon';

export interface CardHeaderProps extends StylerBaseProps {
    /**
     * The card Header content.
     */
    children?: React.ReactNode;

    /**
     * Additional properties applied to the title component.
     */
    TitleProps?: TypographyBodyProps;

    /**
     * Callback fired when the component requests to be removed.
     * If provided, the component will be have a `remove` button.
     * @param {React.SyntheticEvent} event The event source of the callback.
     */
    onRemove?: (event: React.SyntheticEvent) => void;

    /**
     * The text label for the remove button.
     * @default Remove
     */
    removeButtonLabel?: string;
}

export const CardHeader: React.ForwardRefExoticComponent<CardHeaderProps & React.RefAttributes<HTMLElement>> =
    React.forwardRef(function CardHeader(
        props: CardHeaderProps,
        ref: React.ForwardedRef<FixMeLater>
    ): React.JSX.Element | null {
        const { children, title, TitleProps, onRemove, removeButtonLabel = 'Remove', ...other } = props;

        const { styles } = useStyles();
        const { selected } = useCard();

        if (!selected) {
            return null;
        }

        return (
            <div ref={ref} css={styles.root} data-card-header={true} {...other}>
                <div css={styles.content}>
                    <Avatar color="common.white">
                        <TickIcon css={styles.icon} />
                    </Avatar>
                    {children && (
                        <TypographyBody component="div" color="inherit" weight="bold" {...TitleProps}>
                            {children}
                        </TypographyBody>
                    )}
                </div>
                {onRemove && (
                    <Stacker
                        button={true}
                        component="button"
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        onClick={onRemove}
                    >
                        <BinIcon size="s" />
                        <Anchor component="span" size="xs" underline="always">
                            {removeButtonLabel}
                        </Anchor>
                    </Stacker>
                )}
            </div>
        );
    });
